import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { useLmxStore } from './lmxStore';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';

const API = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDERS = [
  'Pedime qué querés crear...',
  'Un reel de 15 s para una cafetería…',
  'Que el personaje hable de frente…',
  'Armá el timeline y exportalo…',
  'Revisá mi proyecto y decime qué mejorar…',
];

const LANGS = [['es', 'ES'], ['en', 'EN']];

/** El panel de vidrio: campo, dictado, voz, permisos y estado. */
export const LmxPanel = ({ panelRef, onSubmit, onGoNode }) => {
  const { getToken } = useAuth();
  const { busy, status, prefs, veil, showLog, log, limits } = useLmxStore();
  const store = useLmxStore;
  const [draft, setDraft] = useState('');
  const [ph, setPh] = useState(0);
  const base = useRef('');
  const lang = prefs?.language || 'es';
  const speech = useSpeechRecognition({ lang: lang === 'en' ? 'en-US' : 'es-AR' });

  useEffect(() => {
    const t = setInterval(() => {
      if (document.activeElement?.dataset?.testid === 'lmx-input') return;
      setPh((i) => (i + 1) % PLACEHOLDERS.length);
    }, 3600);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (speech.listening && speech.transcript) {
      setDraft(`${base.current}${base.current ? ' ' : ''}${speech.transcript}`.trim());
    }
  }, [speech.listening, speech.transcript]);

  const patchPrefs = async (patch) => {
    store.getState().setPrefs(patch);
    try {
      const token = await getToken();
      await fetch(`${API}/api/lmx/me/prefs`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
    } catch {
      toast.error('No se pudo guardar la preferencia');
    }
  };

  const submit = (e) => {
    e?.preventDefault();
    const value = draft.trim();
    if (!value || busy) return;
    setDraft('');
    base.current = '';
    if (speech.listening) speech.stop();
    onSubmit(value);
  };

  const toggleMic = () => {
    if (!speech.supported) {
      toast.info('Tu navegador no permite dictar. Probá con Chrome o Edge.');
      return;
    }
    if (speech.listening) { speech.stop(); return; }
    base.current = draft;
    speech.reset();
    speech.start();
    store.getState().setPose('listen');
  };

  const execOn = !!prefs?.exec_permission;
  const voiceOn = !!prefs?.voice_enabled;

  return (
    <section className="lmx-panel" ref={panelRef} aria-label="Chat con Lumière"
             data-testid="lmx-panel">
      <div className="lmx-composer">
        <form className="lmx-field" onSubmit={submit} autoComplete="off">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={PLACEHOLDERS[ph]}
            aria-label="Mensaje para Lumière"
            disabled={busy}
            data-testid="lmx-input"
          />
          <button className="lmx-send" type="submit" disabled={busy || !draft.trim()}
                  data-testid="lmx-send">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinejoin="round" aria-hidden>
              <path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4 20-7z" />
            </svg>
            Enviar
          </button>
        </form>

        <button
          type="button"
          className={`lmx-mic ${speech.listening ? 'is-on' : ''}`}
          onClick={toggleMic}
          aria-pressed={speech.listening}
          aria-label="Dictar mensaje"
          data-testid="lmx-mic"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="1.8" strokeLinecap="round" aria-hidden>
            <rect x="9" y="3" width="6" height="11" rx="3" />
            <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
          </svg>
        </button>

        <div className={`lmx-voice ${voiceOn ? 'is-on' : ''}`}>
          <div className="lmx-listen-row">
            <span id="lmx-listen-lbl">Háblame</span>
            <span className="lmx-led" aria-hidden />
            <button
              type="button"
              className="lmx-toggle"
              role="switch"
              aria-checked={voiceOn}
              aria-labelledby="lmx-listen-lbl"
              title="Lumière te responde en voz alta"
              onClick={() => {
                patchPrefs({ voice_enabled: !voiceOn });
                if (!voiceOn) toast.info('La voz de Lumière llega en la próxima etapa.');
              }}
              data-testid="lmx-voice-switch"
            >
              <span className="lmx-knob" />
            </button>
          </div>
          <div className="lmx-lang" role="group" aria-label="Idioma de Lumière">
            {LANGS.map(([code, label]) => (
              <button
                key={code}
                type="button"
                aria-pressed={lang === code}
                onClick={() => patchPrefs({ language: code })}
                data-testid={`lmx-lang-${code}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lmx-footer">
        <button
          type="button"
          className="lmx-perm"
          aria-pressed={execOn}
          onClick={() => patchPrefs({ exec_permission: !execOn })}
          data-testid="lmx-perm"
        >
          Permisos de ejecución: {execOn ? 'activados' : 'apagados'}
        </button>

        <button
          type="button"
          className="lmx-veil"
          aria-pressed={veil}
          onClick={() => store.getState().setVeil(!veil)}
          title="Velo detrás del chat"
          data-testid="lmx-veil"
        >
          Velo
        </button>

        <button type="button" className="lmx-veil" onClick={onGoNode}
                data-testid="lmx-gonode">
          Ir al nodo <span className="lmx-kbd">Ctrl K</span>
        </button>

        <button
          type="button"
          className="lmx-log-toggle"
          aria-expanded={showLog}
          onClick={() => store.getState().toggleLog()}
          data-testid="lmx-log-toggle"
        >
          {showLog ? 'Ocultar conversación' : 'Ver conversación'}
        </button>

        <div className={`lmx-status ${status.active ? 'is-active' : ''} ${status.warn ? 'is-warn' : ''}`}
             data-testid="lmx-status">
          <span className="lmx-wave" aria-hidden>
            <i /><i /><i /><i /><i /><i /><i />
          </span>
          <span data-testid="lmx-status-text">{status.text}</span>
          <span className="lmx-line" aria-hidden />
        </div>
      </div>

      {showLog && (
        <div className="lmx-log" data-testid="lmx-log">
          {log.length === 0 && <p><b>Todavía no hablamos.</b></p>}
          {log.map((row, i) => (
            <p key={i}><b>{row.who}: </b>{row.text}</p>
          ))}
          {!!limits?.daily_turn_limit && (
            <p><b>Pedidos de hoy: </b>{limits.turns_today} de {limits.daily_turn_limit}</p>
          )}
        </div>
      )}
    </section>
  );
};

export default LmxPanel;
